import { Injectable } from '@angular/core';
import { IEntityTypeInfo, ISalesforceDisplayTemplates } from 'datasource-service-lib';
import { EntityControllerService } from 'entity-picker-lib';
import { IDataSourceApiService, IEntity, INamedEntity } from 'interfaces-lib';
import { Observable, ReplaySubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SalesforceSharedServiceProviderService } from 'shared-service-provider-lib';
import { renderDisplay } from 'salesforce-display-lib';
import { INamedEntityService } from './inamed-entity.service';

@Injectable({ providedIn: 'root' })
export class SalesforceNamedEntityService implements INamedEntityService {
  private apiService: IDataSourceApiService;
  private entityFieldsByType: Record<string, string[]> = {};
  private displayTemplatesByType: Record<string, ISalesforceDisplayTemplates | undefined> = {};

  namedEntitySubject = new ReplaySubject<INamedEntity>();
  namedEntitySelected$: Observable<INamedEntity> = this.namedEntitySubject.asObservable();

  constructor(private entityControllerService: EntityControllerService, sps: SalesforceSharedServiceProviderService) {
    this.apiService = sps.getDataSourceApiService();
    this.loadEntityFieldMap();
    this.entityControllerService.entitySelected$.subscribe((entity) => {
      if (!entity) {
        return;
      }
      this.getNamedEntity(entity).subscribe((namedEntity) => this.namedEntitySubject.next(namedEntity));
    });
  }

  private loadEntityFieldMap(): void {
    const api = this.apiService as any;
    if (!api.getAvailableEntityTypes) {
      return;
    }
    api.getAvailableEntityTypes().subscribe({
      next: (types: IEntityTypeInfo[]) => {
        this.entityFieldsByType = types.reduce((acc, type) => {
          const fields = type.fields?.length ? type.fields : ['Id'];
          acc[type.objectName] = Array.from(new Set([...fields, 'Id', 'Name']));
          return acc;
        }, {} as Record<string, string[]>);
        this.displayTemplatesByType = types.reduce((acc, type) => {
          acc[type.objectName] = type.display;
          return acc;
        }, {} as Record<string, ISalesforceDisplayTemplates | undefined>);
      },
      error: () => {
        this.entityFieldsByType = {};
        this.displayTemplatesByType = {};
      },
    });
  }

  private getNamedEntity(entity: IEntity): Observable<INamedEntity> {
    return this.apiService.get(entity, { fields: this.getFields(entity) }).pipe(map((fullEntity) => this.entityMappers(fullEntity)));
  }

  private getFields(entity: IEntity): string {
    const type = entity?.type;
    if (!type) {
      return 'Id,Name';
    }
    const fields = this.entityFieldsByType[type];
    if (!fields?.length) {
      return 'Id,Name';
    }
    return Array.from(new Set([...fields, 'Id', 'Name'])).join(',');
  }

  private entityMappers(entityData: any): INamedEntity {
    const template = this.displayTemplatesByType[entityData.type]?.primary;
    const name = renderDisplay(entityData, template);
    return {
      id: entityData.Id,
      type: entityData.type,
      name,
    } as INamedEntity;
  }
}
